import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Input, Select } from "../../../ui";
import { Context } from "../../../../context/Context";
import { formatSar } from "../../../../utils/billingMoney";
import { optionsFromLabels, useBillingAdminText } from "../billingAdminI18n";

const ManualPaymentModal = ({
  loadStores,
  loadInvoices,
  createPayment,
  onCancel,
  onCreated,
}) => {
  const { lang } = useContext(Context);
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [stores, setStores] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("manual_bank_transfer");
  const [reference, setReference] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await loadStores(token);
        setStores(result.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [loadStores, token]);

  const selectedInvoice = useMemo(
    () => invoices.find((invoice) => invoice._id === invoiceId),
    [invoices, invoiceId],
  );

  const selectStore = async (nextStoreId) => {
    setStoreId(nextStoreId);
    setInvoiceId("");
    setInvoices([]);
    if (!nextStoreId) return;
    try {
      const result = await loadInvoices(token, {
        storeId: nextStoreId,
        paymentStatus: "unpaid",
        limit: 100,
      });
      setInvoices(
        (result.data || []).filter((invoice) =>
          ["issued", "partially_paid", "overdue"].includes(invoice.status),
        ),
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const submit = async () => {
    if (saving) return;
    if (!invoiceId || !amount) {
      setError(text.payments.invoiceAndAmountRequired);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createPayment(
        token,
        {
          invoiceId,
          amount,
          method,
          reference,
          idempotencyKey: `manual-${invoiceId}-${Date.now()}`,
        },
        file,
      );
      await onCreated();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="billing-modal-backdrop">
      <div className="billing-modal">
        <h3>{text.payments.recordTitle}</h3>
        <p>{text.payments.recordDescription}</p>
        {error && <div className="billing-error">{error}</div>}
        {loading ? (
          <div className="billing-loading">{text.common.loading}</div>
        ) : (
          <div className="billing-plan-modal-form">
            <Select
              label={text.common.restaurant}
              value={storeId}
              options={stores.map((store) => ({
                value: store.id,
                label: `${store.businessName} (${store.id})`,
              }))}
              onChange={(event) => selectStore(event.target.value)}
              placeholder={text.invoices.selectRestaurant}
            />
            <Select
              label={text.payments.payableInvoice}
              value={invoiceId}
              options={invoices.map((invoice) => ({
                value: invoice._id,
                label: `${invoice.invoiceNumber || text.invoices.draft} - ${formatSar(invoice.balanceDueMinor, lang)}`,
              }))}
              onChange={(event) => setInvoiceId(event.target.value)}
              placeholder={text.payments.selectInvoice}
            />
            {selectedInvoice && (
              <div className="billing-preview-box">
                <div>
                  <span>{text.common.total}</span>
                  <strong>{formatSar(selectedInvoice.totalMinor, lang)}</strong>
                </div>
                <div>
                  <span>{text.common.paid}</span>
                  <strong>
                    {formatSar(selectedInvoice.paidAmountMinor || 0, lang)}
                  </strong>
                </div>
                <div>
                  <span>{text.common.remaining}</span>
                  <strong>
                    {formatSar(selectedInvoice.balanceDueMinor, lang)}
                  </strong>
                </div>
              </div>
            )}
            <div className="billing-form-grid">
              <Input
                label={text.payments.amountSar}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="0.00"
              />
              <Select
                label={text.payments.method}
                value={method}
                options={optionsFromLabels(text.methods, [
                  "manual_bank_transfer",
                  "cash",
                  "other",
                ])}
                onChange={(event) => setMethod(event.target.value)}
              />
            </div>
            <Input
              label={text.payments.reference}
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder={text.payments.transferReference}
            />
            <label className="billing-file-input">
              <span>{text.payments.optionalReceipt}</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
              />
            </label>
            <div className="billing-modal-actions">
              <Button appearance="filled" loading={saving} onClick={submit}>
                {text.payments.createPending}
              </Button>
              <Button appearance="ghost" onClick={onCancel}>
                {text.common.cancel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualPaymentModal;
